import { cn } from '../../lib/utils';

/**
 * Avatar
 *
 * User avatar with image or initials fallback.
 * Follows Raflr design system.
 */

const sizes = {
  xs: 'w-6 h-6 text-2xs',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
  '2xl': 'w-24 h-24 text-2xl',
};

/**
 * Generate initials from a name
 */
function getInitials(name) {
  if (!name) return '?';

  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Generate a consistent color based on name
 */
function getAvatarColor(name) {
  if (!name) return 'bg-gray-400';

  const colors = [
    'bg-primary',
    'bg-secondary',
    'bg-success',
    'bg-warning',
    'bg-error',
    'bg-primary-600',
    'bg-secondary-500',
    'bg-success-600',
  ];

  // Simple hash based on name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

export default function Avatar({
  src,
  alt,
  name,
  size = 'md',
  className,
  ...props
}) {
  const initials = getInitials(name);
  const bgColor = getAvatarColor(name);

  return (
    <div
      className={cn(
        'relative inline-flex items-center justify-center rounded-full overflow-hidden',
        'flex-shrink-0',
        sizes[size],
        !src && bgColor,
        className
      )}
      {...props}
    >
      {src ? (
        <img
          src={src}
          alt={alt || name || 'User avatar'}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Hide broken image, show initials instead
            e.target.style.display = 'none';
          }}
        />
      ) : null}

      {/* Initials fallback - always rendered but hidden when image loads */}
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center',
          'font-medium text-white',
          src && 'hidden'
        )}
      >
        {initials}
      </span>
    </div>
  );
}

/**
 * AvatarGroup
 *
 * Stack multiple avatars with overlap.
 */
export function AvatarGroup({
  users,
  max = 4,
  size = 'sm',
  className,
}) {
  const visibleUsers = users.slice(0, max);
  const remainingCount = users.length - max;

  return (
    <div className={cn('flex -space-x-2', className)}>
      {visibleUsers.map((user, index) => (
        <Avatar
          key={user.id || index}
          src={user.photoURL}
          name={user.displayName}
          size={size}
          className="ring-2 ring-white"
        />
      ))}

      {remainingCount > 0 && (
        <div
          className={cn(
            'inline-flex items-center justify-center rounded-full',
            'bg-gray-200 text-gray-600 font-medium ring-2 ring-white',
            sizes[size]
          )}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
}
